import { Outlet } from 'react-router-dom';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authActions } from '../redux/slices/authSlice.ts';
import { selectAuthInitialized } from '../redux/selectors/commonSelectors.ts';
import { AUTH_PORTAL } from './portal.ts';
import type { UserInfo } from '../types/common/common.ts';

/**
 * Dev user data for local development when VITE_DEV_AUTH=true.
 * Mirrors the seeded local administrator account (demo.user@example.test,
 * aspnet_Roles → "Administrator") and grants every role so all modules —
 * including the Administration sub-modules — are accessible.
 *
 * Every field can be overridden with VITE_DEV_USER_* so a vendor-portal contact
 * can be impersonated locally. Set VITE_DEV_USER_PORTAL=QCDOC-VENDOR together
 * with the matching Auth:DevUser section in the API's appsettings.Development.json.
 *
 * Keep in sync with QCDOC.API/Authentication/DevAuthHandler.cs.
 */
const DEFAULT_ROLES = [
  // Legacy aspnet_Roles name + the canonical roles LegacyRoleMap expands it to
  'Administrator',
  'Admin',
  'SuperAdmin',
  'QCDocAdmin',
  // Remaining module roles so every RoleGuard passes
  'GQL',
  'CM',
  'QALab',
  'QALabSupervisor',
  'QATech',
  'ReadOnly',
  'ProductOwner',
  'PackagingAdmin',
  'COAAdmin',
  'ReportViewer',
  'PurchasingAgent',
  'Manager',
  'GroupLeader',
  'Logistics',
  'SupplyChain',
  'Regulatory',
  'FormulaEditor',
  'Hdfx',
  'Vendor',
  'VendorAdmin',
];

const env = import.meta.env;
const devPortal = env.VITE_DEV_USER_PORTAL || AUTH_PORTAL;
const devUserName = env.VITE_DEV_USER_NAME || 'demo.user@example.test';
const devRoles = env.VITE_DEV_USER_ROLES
  ? env.VITE_DEV_USER_ROLES.split(',').map((r: string) => r.trim()).filter(Boolean)
  : DEFAULT_ROLES;

const MOCK_USER: UserInfo = {
  // Must match an existing dbo.aspnet_Users.UserId — several tables (e.g.
  // NewVendorRequest.RequesterId) have a foreign key onto it.
  sub: env.VITE_DEV_USER_ID || '69DF0AC1-3688-4421-8221-5FDF4B5AEBFB',
  email: devUserName,
  name: env.VITE_DEV_USER_DISPLAY_NAME || 'Dev User',
  preferredUsername: devUserName,
  groups: devRoles,
  psl: env.VITE_DEV_USER_PSL || 'Baroid',
  portal: devPortal,
  isInternal: devPortal !== 'QCDOC-VENDOR',
};

/** Subset of AuthResponse (`GET /api/Auth/me`) needed to identify the dev user. */
interface DevIdentity {
  userId?: string;
  userName?: string;
  email?: string;
  fullName?: string;
  company?: string;
  psl?: string | null;
  portal?: string;
  isInternal?: boolean;
  packaging?: { read: boolean; write: boolean; admin: boolean };
}

/**
 * Adopt the identity the API actually issued.
 *
 * The dev bypass runs on both sides independently: the SPA builds MOCK_USER from
 * VITE_DEV_USER_*, and the API builds its principal from Auth:DevUser. Nothing kept
 * the two in step, so they silently drifted onto different accounts — the SPA thought
 * it was 69DF0AC1 (internal) while the API was authenticating e06cc1ed (vendor). Every
 * ownership check that compares the signed-in id against a stored id then fails for
 * reasons that look like missing permissions: the assigned-Category-Manager buttons on
 * VQCADetailPage never render, and My VQCA queries a different user than the token.
 *
 * Only the *identity* is adopted. Roles stay on the permissive dev list so every
 * RoleGuard keeps passing locally regardless of which account is configured, matching
 * the full set DevAuthHandler already grants as claims.
 */
async function adoptApiIdentity(setUser: (user: UserInfo) => void) {
  try {
    const [{ default: apiClient }, { Endpoints }] = await Promise.all([
      import('../api/common/apiClient.ts'),
      import('@qcdoc/mock-runtime/contracts/endpoints.ts'),
    ]);
    const { data } = await apiClient.get<DevIdentity>(Endpoints.auth.me);
    if (!data?.userId) return;

    setUser({
      ...MOCK_USER,
      sub: data.userId,
      email: data.email || data.userName || MOCK_USER.email,
      name: data.fullName || MOCK_USER.name,
      preferredUsername: data.userName || MOCK_USER.preferredUsername,
      psl: data.psl ?? MOCK_USER.psl,
      portal: data.portal || MOCK_USER.portal,
      packaging: data.packaging,
      isInternal: data.isInternal ?? MOCK_USER.isInternal,
      company: data.company,
    });

    if (data.userId.toLowerCase() !== MOCK_USER.sub.toLowerCase()) {
      console.warn(
        `[DevShell] Adopted API identity ${data.userName} (${data.userId}); ` +
          `VITE_DEV_USER_ID is ${MOCK_USER.sub}. Set Auth:DevUser in the API to change accounts.`,
      );
    }
  } catch {
    // API unreachable — keep the static mock so the SPA still renders offline.
  }
}

/**
 * Development-only shell used when VITE_DEV_AUTH=true.
 */
export default function DevShell() {
  const dispatch = useDispatch();
  const initialized = useSelector(selectAuthInitialized);
  useEffect(() => {
    let cancelled = false;
    dispatch(authActions.setUser(MOCK_USER));
    dispatch(authActions.setInitialized(true));
    void adoptApiIdentity((user) => {
      if (!cancelled) dispatch(authActions.setUser(user));
    });
    return () => { cancelled = true; };
  }, [dispatch]);
  return initialized ? <Outlet /> : null;
}
